import { StakingContractType } from 'enums/staking-contract-type.enum';

import { NETWORK_SETTINGS } from 'constants/network.constant';

import { getStakingContractAbiByType } from 'utils/get-staking-contract-abi-by-type.utils';
import { getStakingContractAddressByType } from 'utils/get-staking-contract-address-by-type.utils';

import { BaseWeb3Service } from './base-web3.service';

/**
 * Callback interface for staking transactions
 */
interface StakingCallback {
  onTransactionHash: (hash: string) => void;
  onConfirmation: () => void;
}

/**
 * Interface for pool user information (whitelisted staking)
 */
export interface IPoolUserInfo {
  apy: string;
  allowedAmountLeft: string;
  remainingAmounts: string[]; // [DT, SV, WC] - game indices
  depositCount: number;
  totalStakedAmount: string;
  totalClaimableInterest: number;
}

/**
 * Service for handling staking contract interactions
 * Supports multiple staking contract types: regular, negative, periodical, and whitelisted
 */
export class StakingService extends BaseWeb3Service {
  private readonly whitelistedStakePoolId = NETWORK_SETTINGS.WHITELISTED_STAKE.POOL_ID;

  /**
   * Creates a staking contract instance by type
   */
  private createStakingContractByType(stakingContractType: StakingContractType): any {
    const address = getStakingContractAddressByType(stakingContractType)!;
    const abi = getStakingContractAbiByType(stakingContractType)!;
    return this.createContract(abi, address);
  }

  // ==================== General Staking Methods ====================

  /**
   * Checks the APY for a specific pool
   */
  async checkAPY(poolId: number, stakingContractType: StakingContractType): Promise<string> {
    const contract = this.createStakingContractByType(stakingContractType);

    await this.updateNetwork();

    return await contract.methods.checkAPY(poolId).call();
  }

  /**
   * Checks the staking fee for a specific pool
   */
  async checkStakingFee(poolId: number, stakingContractType: StakingContractType): Promise<string> {
    const contract = this.createStakingContractByType(stakingContractType);

    await this.updateNetwork();

    return await contract.methods.checkStakingFee(poolId).call();
  }

  /**
   * Checks the default minimum deposit amount
   */
  async checkDefaultMinimumDeposit(stakingContractType: StakingContractType): Promise<string> {
    const contract = this.createStakingContractByType(stakingContractType);

    await this.updateNetwork();

    return await contract.methods.checkDefaultMinimumDeposit().call();
  }

  /**
   * Checks the amount staked by a wallet in a pool
   */
  async checkStakedAmountBy(
    walletAddress: string,
    poolId: number,
    stakingContractType: StakingContractType
  ): Promise<string> {
    const contract = this.createStakingContractByType(stakingContractType);

    await this.updateNetwork();

    return await contract.methods.checkStakedAmountBy(walletAddress, poolId).call();
  }

  /**
   * Checks if staking is open for a pool
   */
  async checkIfStakingOpen(poolId: number, stakingContractType: StakingContractType): Promise<boolean> {
    const contract = this.createStakingContractByType(stakingContractType);

    await this.updateNetwork();

    return await contract.methods.checkIfStakingOpen(poolId).call();
  }

  /**
   * Checks if a pool has ended
   */
  async checkIfPoolEnded(poolId: number, stakingContractType: StakingContractType): Promise<boolean> {
    const contract = this.createStakingContractByType(stakingContractType);

    await this.updateNetwork();

    return await contract.methods.checkIfPoolEnded(poolId).call();
  }

  /**
   * Checks the pool type
   */
  async checkPoolType(poolId: number, stakingContractType: StakingContractType): Promise<string> {
    const contract = this.createStakingContractByType(stakingContractType);

    await this.updateNetwork();

    return await contract.methods.checkPoolType(poolId).call();
  }

  /**
   * Checks the staking target for a pool
   */
  async checkStakingTarget(poolId: number, stakingContractType: StakingContractType): Promise<string> {
    const contract = this.createStakingContractByType(stakingContractType);

    await this.updateNetwork();

    return await contract.methods.checkStakingTarget(poolId).call();
  }

  /**
   * Checks the total amount staked in a pool
   */
  async checkTotalStaked(poolId: number, stakingContractType: StakingContractType): Promise<string> {
    const contract = this.createStakingContractByType(stakingContractType);

    await this.updateNetwork();

    return await contract.methods.checkTotalStaked(poolId).call();
  }

  /**
   * Gets the staking token address for a contract type
   */
  async stakingToken(stakingContractType: StakingContractType): Promise<string> {
    const contract = this.createStakingContractByType(stakingContractType);

    await this.updateNetwork();

    return await contract.methods.STAKING_TOKEN().call();
  }

  /**
   * Stakes tokens with safe fee check
   */
  async safeStake(
    walletAddress: string,
    poolId: number,
    tokenAmount: string,
    forMaxFeePercentage: string,
    stakingContractType: StakingContractType,
    callback: StakingCallback
  ): Promise<any> {
    const contract = this.createStakingContractByType(stakingContractType);
    const gasPrice = await this.getGasPrice();

    await this.updateNetwork();

    return await contract.methods
      .safeStake(poolId, tokenAmount, forMaxFeePercentage)
      .send({ from: walletAddress, gasPrice })
      .on('transactionHash', (hash: string) => callback.onTransactionHash(hash))
      .on('confirmation', (blocks: number) => this.isConfirmed(blocks) && callback.onConfirmation());
  }

  /**
   * Withdraws all staked tokens from a pool
   */
  async withdrawAll(
    walletAddress: string,
    poolId: number,
    stakingContractType: StakingContractType,
    callback: StakingCallback
  ): Promise<any> {
    const contract = this.createStakingContractByType(stakingContractType);
    const gasPrice = await this.getGasPrice();

    await this.updateNetwork();

    return await contract.methods
      .withdrawAll(poolId)
      .send({ from: walletAddress, gasPrice })
      .on('transactionHash', (hash: string) => callback.onTransactionHash(hash))
      .on('confirmation', (blocks: number) => this.isConfirmed(blocks) && callback.onConfirmation());
  }

  // ==================== Regular Stake Methods ====================

  /**
   * Checks generated interest for the last day
   */
  async checkGeneratedInterestLastDayFor(walletAddress: string, poolId: number): Promise<string> {
    const contract = this.createStakingContractByType(StakingContractType.STANDARD);

    await this.updateNetwork();

    return await contract.methods.checkGeneratedInterestLastDayFor(walletAddress, poolId).call();
  }

  /**
   * Checks if interest claim is open
   */
  async checkIfInterestClaimOpen(poolId: number): Promise<boolean> {
    const contract = this.createStakingContractByType(StakingContractType.STANDARD);

    await this.updateNetwork();

    return await contract.methods.checkIfInterestClaimOpen(poolId).call();
  }

  /**
   * Checks total claimable interest for a wallet
   */
  async checkTotalClaimableInterestBy(walletAddress: string, poolId: number): Promise<string> {
    const contract = this.createStakingContractByType(StakingContractType.STANDARD);

    await this.updateNetwork();

    return await contract.methods.checkTotalClaimableInterestBy(walletAddress, poolId).call();
  }

  /**
   * Claims all interest from a pool
   */
  async claimAllInterest(walletAddress: string, poolId: number, callback: StakingCallback): Promise<any> {
    const contract = this.createStakingContractByType(StakingContractType.STANDARD);
    const gasPrice = await this.getGasPrice();

    await this.updateNetwork();

    return await contract.methods
      .claimAllInterest(poolId)
      .send({ from: walletAddress, gasPrice })
      .on('transactionHash', (hash: string) => callback.onTransactionHash(hash))
      .on('confirmation', (blocks: number) => this.isConfirmed(blocks) && callback.onConfirmation());
  }

  /**
   * Stakes tokens (simple method without fee check)
   */
  async stakeToken(
    walletAddress: string,
    poolId: number,
    tokenAmount: string,
    callback: StakingCallback
  ): Promise<any> {
    const contract = this.createStakingContractByType(StakingContractType.STANDARD);
    const gasPrice = await this.getGasPrice();

    await this.updateNetwork();

    return await contract.methods
      .stakeToken(poolId, tokenAmount)
      .send({ from: walletAddress, gasPrice })
      .on('transactionHash', (hash: string) => callback.onTransactionHash(hash))
      .on('confirmation', (blocks: number) => this.isConfirmed(blocks) && callback.onConfirmation());
  }

  // ==================== Negative Stake (Rewards) Methods ====================

  /**
   * Checks generated reward for the last day
   */
  async checkGeneratedRewardLastDayFor(walletAddress: string, poolId: number): Promise<string> {
    const contract = this.createStakingContractByType(StakingContractType.NEGATIVE);

    await this.updateNetwork();

    return await contract.methods.checkGeneratedRewardLastDayFor(walletAddress, poolId).call();
  }

  /**
   * Checks if reward claim is open
   */
  async checkIfRewardClaimOpen(poolId: number): Promise<boolean> {
    const contract = this.createStakingContractByType(StakingContractType.NEGATIVE);

    await this.updateNetwork();

    return await contract.methods.checkIfRewardClaimOpen(poolId).call();
  }

  /**
   * Checks total claimable reward for a wallet
   */
  async checkTotalClaimableRewardBy(walletAddress: string, poolId: number): Promise<string> {
    const contract = this.createStakingContractByType(StakingContractType.NEGATIVE);

    await this.updateNetwork();

    return await contract.methods.checkTotalClaimableRewardBy(walletAddress, poolId).call();
  }

  /**
   * Checks if withdrawal is open
   */
  async checkIfWithdrawalOpen(poolId: number, stakingContractType: StakingContractType): Promise<boolean> {
    const contract = this.createStakingContractByType(stakingContractType);

    await this.updateNetwork();

    return await contract.methods.checkIfWithdrawalOpen(poolId).call();
  }

  /**
   * Claims all rewards from a pool
   */
  async claimAllReward(walletAddress: string, poolId: number, callback: StakingCallback): Promise<any> {
    const contract = this.createStakingContractByType(StakingContractType.NEGATIVE);
    const gasPrice = await this.getGasPrice();

    await this.updateNetwork();

    return await contract.methods
      .claimAllReward(poolId)
      .send({ from: walletAddress, gasPrice })
      .on('transactionHash', (hash: string) => callback.onTransactionHash(hash))
      .on('confirmation', (blocks: number) => this.isConfirmed(blocks) && callback.onConfirmation());
  }

  /**
   * Gets the reward token address
   */
  async rewardToken(): Promise<string> {
    const contract = this.createStakingContractByType(StakingContractType.NEGATIVE);

    await this.updateNetwork();

    return await contract.methods.REWARD_TOKEN().call();
  }

  // ==================== Periodical Stake Methods ====================

  /**
   * Gets the periodical staking token address
   */
  async periodicalStakingToken(): Promise<string> {
    const contract = this.createStakingContractByType(StakingContractType.PERIODICAL);
    return await contract.methods.STAKING_TOKEN().call();
  }

  /**
   * Gets the minimum deposit for periodical staking
   */
  async periodicalStakingMinimumDeposit(): Promise<string> {
    const contract = this.createStakingContractByType(StakingContractType.PERIODICAL);
    return await contract.methods.minimumDeposit().call();
  }

  /**
   * Stakes tokens periodically with safe checks
   */
  async periodicalSafeStake(
    walletAddress: string,
    stakingPhase: string,
    stakingPeriod: string,
    tokenAmount: string,
    expectedAPY: string,
    callback: StakingCallback
  ): Promise<any> {
    const contract = this.createStakingContractByType(StakingContractType.PERIODICAL);
    const gasPrice = await this.getGasPrice();

    await this.updateNetwork();

    return await contract.methods
      .safeStake(stakingPhase, stakingPeriod, tokenAmount, expectedAPY)
      .send({ from: walletAddress, gasPrice })
      .on('transactionHash', (hash: string) => callback.onTransactionHash(hash))
      .on('confirmation', (blocks: number) => this.isConfirmed(blocks) && callback.onConfirmation());
  }

  /**
   * Gets periodical staking program data
   */
  async getPeriodicalStakingData(): Promise<any> {
    const contract = this.createStakingContractByType(StakingContractType.PERIODICAL);
    return await contract.methods.getProgramData().call();
  }

  /**
   * Gets deposit count for a wallet in periodical staking
   */
  async periodicalStakingDepositCount(walletAddress: string): Promise<number> {
    const contract = this.createStakingContractByType(StakingContractType.PERIODICAL);
    return await contract.methods.checkDepositCountOfAddress(walletAddress).call();
  }

  /**
   * Gets deposits in a specific range for periodical staking
   */
  async getPeriodicalStakingDepositsInRange(walletAddress: string, from: number, to: number): Promise<any> {
    const contract = this.createStakingContractByType(StakingContractType.PERIODICAL);
    return await contract.methods.getDepositsInRangeBy(walletAddress, from, to).call();
  }

  /**
   * Gets user data for periodical staking
   */
  async periodicalStakingGetUserData(dataType: number, walletAddress: string): Promise<any> {
    const contract = this.createStakingContractByType(StakingContractType.PERIODICAL);
    return await contract.methods.getUserData(dataType, walletAddress).call();
  }

  /**
   * Checks claimable data for a wallet in periodical staking
   */
  async periodicalStakingCheckClaimableDataFor(walletAddress: string): Promise<any> {
    const contract = this.createStakingContractByType(StakingContractType.PERIODICAL);
    return await contract.methods.checkClaimableDataFor(walletAddress).call();
  }

  /**
   * Claims all from periodical staking
   */
  async periodicalStakingClaimAll(walletAddress: string, callback: StakingCallback): Promise<any> {
    const contract = this.createStakingContractByType(StakingContractType.PERIODICAL);
    const gasPrice = await this.getGasPrice();

    await this.updateNetwork();

    return await contract.methods
      .claimAll()
      .send({ from: walletAddress, gasPrice })
      .on('transactionHash', (hash: string) => callback.onTransactionHash(hash))
      .on('confirmation', (blocks: number) => this.isConfirmed(blocks) && callback.onConfirmation());
  }

  /**
   * Claims a specific deposit from periodical staking
   */
  async periodicalStakingClaim(walletAddress: string, depositNumber: number, callback: StakingCallback): Promise<any> {
    const contract = this.createStakingContractByType(StakingContractType.PERIODICAL);
    const gasPrice = await this.getGasPrice();

    await this.updateNetwork();

    return await contract.methods
      .claimDeposit(depositNumber)
      .send({ from: walletAddress, gasPrice })
      .on('transactionHash', (hash: string) => callback.onTransactionHash(hash))
      .on('confirmation', (blocks: number) => this.isConfirmed(blocks) && callback.onConfirmation());
  }

  /**
   * Withdraws a specific deposit from periodical staking
   */
  async periodicalStakingWithdraw(
    walletAddress: string,
    depositNumber: number,
    callback: StakingCallback
  ): Promise<any> {
    const contract = this.createStakingContractByType(StakingContractType.PERIODICAL);
    const gasPrice = await this.getGasPrice();

    await this.updateNetwork();

    return await contract.methods
      .withdrawDeposit(depositNumber)
      .send({ from: walletAddress, gasPrice })
      .on('transactionHash', (hash: string) => callback.onTransactionHash(hash))
      .on('confirmation', (blocks: number) => this.isConfirmed(blocks) && callback.onConfirmation());
  }

  // ==================== Whitelisted Stake Methods ====================

  /**
   * Gets the whitelisted staking token address
   */
  async whitelistedStakingToken(): Promise<string> {
    const contract = this.createStakingContractByType(StakingContractType.WHITELISTED);
    return await contract.methods.STAKING_TOKEN().call();
  }

  /**
   * Checks pool user info for whitelisted staking
   */
  async checkPoolUserInfo(userAddress: string): Promise<IPoolUserInfo> {
    const contract = this.createStakingContractByType(StakingContractType.WHITELISTED);
    return await contract.methods.checkPoolUserInfo(userAddress, this.whitelistedStakePoolId).call();
  }

  /**
   * Gets deposits in range for whitelisted staking
   */
  async getWhitelistedDepositsInRange(userAddress: string, fromIndex: number, toIndex: number): Promise<any> {
    const contract = this.createStakingContractByType(StakingContractType.WHITELISTED);
    return await contract.methods
      .getDepositsInRangeFor(userAddress, this.whitelistedStakePoolId, fromIndex, toIndex)
      .call();
  }

  /**
   * Stakes tokens in whitelisted pool
   */
  async whitelistedStakeToken(walletAddress: string, tokenAmount: string, callback: StakingCallback): Promise<any> {
    const contract = this.createStakingContractByType(StakingContractType.WHITELISTED);
    const gasPrice = await this.getGasPrice();

    await this.updateNetwork();

    return await contract.methods
      .stakeToken(this.whitelistedStakePoolId, tokenAmount)
      .send({ from: walletAddress, gasPrice })
      .on('transactionHash', (hash: string) => callback.onTransactionHash(hash))
      .on('confirmation', (blocks: number) => this.isConfirmed(blocks) && callback.onConfirmation());
  }

  /**
   * Claims interest from a specific deposit in whitelisted pool
   */
  async whitelistedClaimInterest(
    walletAddress: string,
    depositNumber: number,
    callback: StakingCallback
  ): Promise<any> {
    const contract = this.createStakingContractByType(StakingContractType.WHITELISTED);
    const gasPrice = await this.getGasPrice();

    await this.updateNetwork();

    return await contract.methods
      .claimInterest(this.whitelistedStakePoolId, depositNumber)
      .send({ from: walletAddress, gasPrice })
      .on('transactionHash', (hash: string) => callback.onTransactionHash(hash))
      .on('confirmation', (blocks: number) => this.isConfirmed(blocks) && callback.onConfirmation());
  }

  /**
   * Withdraws a specific deposit from whitelisted pool
   */
  async whitelistedWithdrawDeposit(
    walletAddress: string,
    depositNumber: number,
    callback: StakingCallback
  ): Promise<any> {
    const contract = this.createStakingContractByType(StakingContractType.WHITELISTED);
    const gasPrice = await this.getGasPrice();

    await this.updateNetwork();

    return await contract.methods
      .withdrawDeposit(this.whitelistedStakePoolId, depositNumber)
      .send({ from: walletAddress, gasPrice })
      .on('transactionHash', (hash: string) => callback.onTransactionHash(hash))
      .on('confirmation', (blocks: number) => this.isConfirmed(blocks) && callback.onConfirmation());
  }

  /**
   * Claims all interest from whitelisted pool
   */
  async whitelistedClaimAllInterest(walletAddress: string, callback: StakingCallback): Promise<any> {
    const contract = this.createStakingContractByType(StakingContractType.WHITELISTED);
    const gasPrice = await this.getGasPrice();

    await this.updateNetwork();

    return await contract.methods
      .claimAllInterest(this.whitelistedStakePoolId)
      .send({ from: walletAddress, gasPrice })
      .on('transactionHash', (hash: string) => callback.onTransactionHash(hash))
      .on('confirmation', (blocks: number) => this.isConfirmed(blocks) && callback.onConfirmation());
  }

  /**
   * Gets deposit count for whitelisted staking
   */
  async whitelistedStakingDepositCount(walletAddress: string): Promise<number> {
    const contract = this.createStakingContractByType(StakingContractType.WHITELISTED);
    return await contract.methods.checkDepositCountOfAddress(walletAddress, this.whitelistedStakePoolId).call();
  }

  /**
   * Checks allowed amount left for a user in whitelisted staking
   */
  async checkAllowedAmountLeftFor(userAddress: string): Promise<string> {
    const contract = this.createStakingContractByType(StakingContractType.WHITELISTED);
    return await contract.methods.checkAllowedAmountLeftFor(userAddress, this.whitelistedStakePoolId).call();
  }

  /**
   * Checks withdrawn amount by a user in whitelisted staking
   */
  async checkWithdrawnAmountBy(userAddress: string): Promise<string> {
    const contract = this.createStakingContractByType(StakingContractType.WHITELISTED);
    return await contract.methods.checkWithdrawnAmountBy(userAddress, this.whitelistedStakePoolId).call();
  }

  // ==================== VIP Stake Methods ====================

  /**
   * Gets VIP staking program data with user data (v0.3.2+)
   * Returns: currentPhase, periods, targets, APYs, staked, requiredWorth, limits, remaining, eligibilities
   */
  async getVipProgramDataWithUserData(userAddress: string): Promise<{
    currentPhase: string;
    periods: string[];
    targets: string[][];
    apys: string[][];
    staked: string[][];
    requiredWorth: string[][];
    limits: string[][];
    remaining: string[][];
    eligibilities: boolean[][];
  }> {
    const contract = this.createStakingContractByType(StakingContractType.VIP);
    const result = await contract.methods.getProgramDataWithUserData(userAddress).call();

    return {
      currentPhase: result[0],
      periods: result[1],
      targets: result[2],
      apys: result[3],
      staked: result[4],
      requiredWorth: result[5],
      limits: result[6],
      remaining: result[7],
      eligibilities: result[8],
    };
  }

  /**
   * Gets VIP staking program data (without user-specific data)
   */
  async getVipProgramData(userAddress: string): Promise<any> {
    const contract = this.createStakingContractByType(StakingContractType.VIP);
    return await contract.methods.getProgramData(userAddress).call();
  }

  /**
   * Gets the minimum deposit for VIP staking
   */
  async vipStakingMinimumDeposit(): Promise<string> {
    const contract = this.createStakingContractByType(StakingContractType.VIP);
    return await contract.methods.minimumDeposit().call();
  }

  /**
   * Stakes tokens in VIP pool with safe checks
   */
  async vipSafeStake(
    walletAddress: string,
    stakingPhase: string,
    stakingPeriod: string,
    tokenAmount: string,
    expectedAPY: string,
    callback: StakingCallback
  ): Promise<any> {
    const contract = this.createStakingContractByType(StakingContractType.VIP);
    const gasPrice = await this.getGasPrice();

    await this.updateNetwork();

    return await contract.methods
      .safeStake(stakingPhase, stakingPeriod, tokenAmount, expectedAPY)
      .send({ from: walletAddress, gasPrice })
      .on('transactionHash', (hash: string) => callback.onTransactionHash(hash))
      .on('confirmation', (blocks: number) => this.isConfirmed(blocks) && callback.onConfirmation());
  }

  /**
   * Gets deposit count for VIP staking
   */
  async vipStakingDepositCount(walletAddress: string): Promise<number> {
    const contract = this.createStakingContractByType(StakingContractType.VIP);
    return await contract.methods.checkDepositCountOfAddress(walletAddress).call();
  }

  /**
   * Gets deposits in a specific range for VIP staking
   */
  async getVipStakingDepositsInRange(walletAddress: string, from: number, to: number): Promise<any> {
    const contract = this.createStakingContractByType(StakingContractType.VIP);
    return await contract.methods.getDepositsInRangeBy(walletAddress, from, to).call();
  }

  /**
   * Claims all from VIP staking
   */
  async vipStakingClaimAll(walletAddress: string, callback: StakingCallback): Promise<any> {
    const contract = this.createStakingContractByType(StakingContractType.VIP);
    const gasPrice = await this.getGasPrice();

    await this.updateNetwork();

    return await contract.methods
      .claimAll()
      .send({ from: walletAddress, gasPrice })
      .on('transactionHash', (hash: string) => callback.onTransactionHash(hash))
      .on('confirmation', (blocks: number) => this.isConfirmed(blocks) && callback.onConfirmation());
  }

  /**
   * Claims a specific deposit from VIP staking
   */
  async vipStakingClaim(walletAddress: string, depositNumber: number, callback: StakingCallback): Promise<any> {
    const contract = this.createStakingContractByType(StakingContractType.VIP);
    const gasPrice = await this.getGasPrice();

    await this.updateNetwork();

    return await contract.methods
      .claimDeposit(depositNumber)
      .send({ from: walletAddress, gasPrice })
      .on('transactionHash', (hash: string) => callback.onTransactionHash(hash))
      .on('confirmation', (blocks: number) => this.isConfirmed(blocks) && callback.onConfirmation());
  }

  /**
   * Withdraws a specific deposit from VIP staking
   */
  async vipStakingWithdraw(walletAddress: string, depositNumber: number, callback: StakingCallback): Promise<any> {
    const contract = this.createStakingContractByType(StakingContractType.VIP);
    const gasPrice = await this.getGasPrice();

    await this.updateNetwork();

    return await contract.methods
      .withdrawDeposit(depositNumber)
      .send({ from: walletAddress, gasPrice })
      .on('transactionHash', (hash: string) => callback.onTransactionHash(hash))
      .on('confirmation', (blocks: number) => this.isConfirmed(blocks) && callback.onConfirmation());
  }

  /**
   * Checks if user meets VIP eligibility requirements
   */
  async checkVipEligibility(userAddress: string): Promise<boolean> {
    const contract = this.createStakingContractByType(StakingContractType.VIP);
    return await contract.methods.checkIfUserMeetsRequirements(userAddress, false).call();
  }
}
